// Логика черновика отметки косяка: получасовая точность времени + память
// последнего вида/времени на 30 минут (быстрая отметка нескольких точек подряд).
// Чистые функции — тестируются без DOM; localStorage обёрнут отдельно.

export type SizeClass = 'small' | 'medium' | 'large'

export const SIZE_OPTIONS: { value: SizeClass; labelKey: string }[] = [
  { value: 'small', labelKey: 'models.cluster.size.small' },
  { value: 'medium', labelKey: 'models.cluster.size.medium' },
  { value: 'large', labelKey: 'models.cluster.size.large' },
]

// Радиус кружка косяка на карте по классу размера (пиксели). null → средне-нейтральный.
/** Цвет маркера косяка — ЛАЗУРНЫЙ. Им же рисуется трек сноса (trackStyle.TRACK_COLOR):
 *  путь и его точки должны читаться как одно целое. Был зелёным как контраст к
 *  фиолетовому фону viridis-течений; с 2026-07-28 течения красятся «чёрный→зелёный»,
 *  поэтому зелёная отметка слилась бы со струёй. Взят лазурный #0ea5e9, а не курс-
 *  синь #2563eb: та занята маркером живого судна — косяк должен отличаться от судна. */
export const CLUSTER_COLOR = '#0ea5e9'

/** Прозрачность отметки косяка (её ИСХОДНОГО положения).
 *
 *  При включённом сносе на карте два маркера одного косяка: отметка там, где
 *  его увидели, и снесённая точка на выбранный слайдером день. Ответ на вопрос
 *  «где косяк сейчас» — второй, поэтому первый уводится в фон. Совсем убирать
 *  нельзя: по нему кликают, чтобы открыть правку. */
export function clusterOpacity(driftOn: boolean): number {
  return driftOn ? 0.3 : 0.85
}

export function clusterRadius(size: SizeClass | null | undefined): number {
  switch (size) {
    case 'small':
      return 5
    case 'medium':
      return 8
    case 'large':
      return 12
    default:
      return 6
  }
}

/**
 * Косяки для показа на карте в режиме «течения»/дрейф. Каждая точка привязана
 * к своему виду: при выбранном виде показываем ТОЛЬКО косяки этого вида (косяки
 * без вида, species_id=null, при этом скрыты), без выбора вида — все.
 */
export function visibleClusters<
  T extends { species_id: number | null; observed_at?: string | null },
>(clusters: T[], species: number | null, date?: string): T[] {
  let out = clusters
  if (species != null) out = out.filter(c => c.species_id === species)
  // Ворота по выбранному дню: отметка, поставленная позже, тогда не существовала.
  // Отметку без даты наблюдения сравнивать не с чем — оставляем.
  if (date) {
    out = out.filter(c => !c.observed_at || c.observed_at.slice(0, 10) <= date)
  }
  return out
}

// Дата-время автоподставляется из прошлой отметки только 30 минут (req 3).
export const MEMORY_TTL_MS = 30 * 60 * 1000

/** Округление ВНИЗ до получаса: минуты → 0/30, секунды/мс → 0 (локальное время). */
export function roundDownToHalfHour(d: Date): Date {
  const r = new Date(d)
  r.setMinutes(r.getMinutes() < 30 ? 0 : 30, 0, 0)
  return r
}

/** 48 получасовых слотов «HH:MM» (00:00 … 23:30). */
export function halfHourSlots(): string[] {
  const out: string[] = []
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    }
  }
  return out
}

/** Локальная дата «YYYY-MM-DD» (не UTC — настенная дата пользователя). */
export function localDateStr(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Локальный получасовой слот «HH:MM» из Date (минуты приводятся к 00/30). */
export function localSlot(d: Date): string {
  const r = roundDownToHalfHour(d)
  return `${String(r.getHours()).padStart(2, '0')}:${String(r.getMinutes()).padStart(2, '0')}`
}

/** Собрать локальный Date из «YYYY-MM-DD» и слота «HH:MM». */
export function composeLocal(dateStr: string, slot: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  const [hh, mm] = slot.split(':').map(Number)
  return new Date(y, m - 1, d, hh, mm, 0, 0)
}

export interface ClusterMemory {
  speciesId: number | null
  observedISO: string // момент наблюдения (ISO, TZ-aware)
  savedAtMs: number // wall-clock последнего сохранения
}

export interface ClusterDraft {
  speciesId: number | null
  observedAt: Date
}

/**
 * Предзаполнение новой формы (req 3):
 * • вид — последний сохранённый (без ограничения по времени);
 * • дата-время — последнее, если с сохранения прошло < 30 мин; иначе текущее
 *   время, округлённое вниз до получаса.
 */
export function nextDraft(now: Date, mem: ClusterMemory | null): ClusterDraft {
  if (mem && now.getTime() - mem.savedAtMs < MEMORY_TTL_MS) {
    return { speciesId: mem.speciesId, observedAt: new Date(mem.observedISO) }
  }
  return { speciesId: mem?.speciesId ?? null, observedAt: roundDownToHalfHour(now) }
}

const MEMORY_KEY = 'cabinet.clusterDraft'

export function loadMemory(): ClusterMemory | null {
  try {
    const raw = localStorage.getItem(MEMORY_KEY)
    if (!raw) return null
    const m = JSON.parse(raw)
    if (typeof m?.observedISO === 'string' && typeof m?.savedAtMs === 'number') {
      return { speciesId: m.speciesId ?? null, observedISO: m.observedISO, savedAtMs: m.savedAtMs }
    }
    return null
  } catch {
    return null
  }
}

export function saveMemory(m: ClusterMemory): void {
  try {
    localStorage.setItem(MEMORY_KEY, JSON.stringify(m))
  } catch {
    // недоступный localStorage — память просто не сохранится
  }
}
